import React, { useCallback } from "react"
import { Button, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faXmark } from "@fortawesome/free-solid-svg-icons"
import { BuilderDropZone } from "shared/ui/Builder"
import {
  useQueryBuilderStore,
  useActiveBuilder,
} from "stores/queryBuilderStore"

/**
 * Compound filter builder. Each group is OR'd internally; groups AND across
 * each other — emitted as an FFIELD on top of the active builder's crosstab.
 *
 * Empty by default (no groups). User clicks "Add condition" to start. Once
 * any group has variables, the script gets an FFIELD prefix and the XTAB
 * UNIVERSE clause uses that boolean field.
 */
const BuilderConditions: React.FC = () => {
  const builder = useActiveBuilder()
  const addFilterGroup = useQueryBuilderStore((s) => s.addFilterGroup)
  const removeFilterGroup = useQueryBuilderStore((s) => s.removeFilterGroup)
  const addFilterGroupVariable = useQueryBuilderStore(
    (s) => s.addFilterGroupVariable
  )
  const removeFilterGroupVariable = useQueryBuilderStore(
    (s) => s.removeFilterGroupVariable
  )

  const groups = builder?.filterGroups ?? []

  const handleAddVar = useCallback(
    (groupId: string) => (variable: string) =>
      addFilterGroupVariable(groupId, variable),
    [addFilterGroupVariable]
  )
  const handleRemoveVar = useCallback(
    (groupId: string) => (variable: string) =>
      removeFilterGroupVariable(groupId, variable),
    [removeFilterGroupVariable]
  )

  return (
    <div className="qc-conditions">
      {groups.length === 0 ? (
        <Button
          type="dashed"
          block
          icon={<FontAwesomeIcon icon={faPlus} />}
          onClick={() => addFilterGroup()}
        >
          Add condition
        </Button>
      ) : (
        <>
          {groups.map((group, idx) => (
            <React.Fragment key={group.id}>
              {idx > 0 && (
                <div className="qc-conditions__connector" aria-hidden>
                  AND
                </div>
              )}
              <div className="qc-conditions__group">
                <Tooltip title="Remove condition" placement="top">
                  <button
                    type="button"
                    className="qc-conditions__remove"
                    aria-label="Remove condition group"
                    onClick={() => removeFilterGroup(group.id)}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </Tooltip>
                <BuilderDropZone
                  label={`Group ${idx + 1}`}
                  variables={group.variables}
                  onAdd={handleAddVar(group.id)}
                  onRemove={handleRemoveVar(group.id)}
                  emptyHint="Drag values that satisfy this condition"
                  expandParents
                />
              </div>
            </React.Fragment>
          ))}
          <Button
            type="dashed"
            className="self-start"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => addFilterGroup()}
          >
            Add another condition
          </Button>
        </>
      )}
    </div>
  )
}

export default BuilderConditions
